import { fetchMessages } from "../sms/smsApi";
import type { SmsIndexEntry } from "./tagCall";

// Phase 2.5 — cross-module unlock for the "Sent a Text" composite tag.
//
// `tagCall` treats its `SmsIndexEntry[]` as "outbound SMS from our staff
// lines, each with a contact number and a send time, so it can answer
// the question: did we text this caller within ±60 min of their call?"
//
// This helper builds that index from /sms/messages for a date window.
// It's shared between:
//   - /voice/calls (CallsView) — passes the index into the view so the
//     composite "Sent a Text" badge lights up next to primary tags
//   - /reports (ReportsModule) — needs the same index so the Reports
//     "Sent a Text" count matches what operators see in /voice/calls
//
// Graceful degradation: if the fetch fails (e.g. a voice-only user has
// no `sms` module and the gateway returns 403), we return `[]`. The
// view still renders — it just won't light up the composite badge.
// That's strictly better than error-ing the whole Calls page.
//
// Window semantics match CallsView's toIsoEndExclusive: `toIso` is the
// exclusive upper bound (one day past the last date the operator picked).

const SENT_TEXT_HALO_MS = 60 * 60 * 1000;

export async function buildSmsIndex(params: {
  fromIso: string;
  toIso: string;
}): Promise<SmsIndexEntry[]> {
  // Widen the fetch by the ±60 min halo `tagCall` allows, so a call at
  // 11:55pm on the last day of the window still sees any outbound SMS
  // we sent at 12:30am the next day. Without the halo we'd systematically
  // undercount "Sent a Text" at window boundaries.
  const widenedFrom = new Date(Date.parse(params.fromIso) - SENT_TEXT_HALO_MS).toISOString();
  const widenedTo = new Date(Date.parse(params.toIso) + SENT_TEXT_HALO_MS).toISOString();

  try {
    const rows = await fetchMessages({
      from: widenedFrom,
      to: widenedTo,
      limit: 2000,
    });
    const index: SmsIndexEntry[] = [];
    for (const m of rows) {
      if (m.direction !== "outbound") continue;
      const ms = Date.parse(m.created_at);
      if (!Number.isFinite(ms)) continue;
      index.push({ to_number: m.to_number, created_at_ms: ms });
    }
    return index;
  } catch {
    // Fail-closed: return an empty index so the view renders without
    // the composite badge rather than throwing a full page error.
    return [];
  }
}
